import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { VipService } from '../shared/services/vip.service';
import { BroadcastService } from '../shared/services/broadcast.service';
import { AiChatSessionService } from '../shared/services/ai-chat-session.service';

const TOKEN_KEY = 'app-token';
const UID_KEY = 'uid';
const USER_INFO_KEY = 'user-info';
const ROLE_KEY = 'app-role';
const ONBOARDING_STATUS_KEY = 'app-onboarding-status';

/**
 * 认证 & 缓存服务
 *
 * 对应 study-plan 项目 `utils/cache.ts` 的核心能力：
 * - Token（用于 Authorization Header）
 * - UID（用户唯一标识）
 * - 用户信息存取
 * - 清除登录态
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  // 账号相关内存缓存（VIP 状态 60s 短缓存 / 喊话权限），登出或切换 token 时必须清空，
  // 否则同浏览器先登录会员再登非会员会串号（仍显示上一账号状态）
  private readonly vipService = inject(VipService);
  private readonly broadcastService = inject(BroadcastService);
  // AI 助手会话同样是 root 单例、同样只存在内存里，登出/换号时必须一起清空：
  // 聊天记录里含 PII 卡片（联系电话、家庭住址原值），而 router.navigate 不会销毁 SPA，
  // 不清的话下一位登录的教师会直接看到上一位教师的对话与卡片。
  private readonly aiChatSession = inject(AiChatSessionService);

  // ==========================================
  //  Token
  // ==========================================

  /** 设置 token（新登录：清空上一账号的内存缓存，防串号） */
  setToken(token: string): void {
    this.vipService.clearCache();
    this.broadcastService.clearCache();
    this.aiChatSession.clear();
    localStorage.setItem(TOKEN_KEY, token);
  }

  /** 获取 token */
  getToken(): string {
    return localStorage.getItem(TOKEN_KEY) || '';
  }

  /** 清除 token */
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  }

  // ==========================================
  //  UID
  // ==========================================

  /** 设置 UID */
  setUID(uid: string): void {
    localStorage.setItem(UID_KEY, uid);
  }

  /** 获取 UID */
  getUID(): string {
    return localStorage.getItem(UID_KEY) || '';
  }

  /** 清除 UID */
  clearUID(): void {
    localStorage.removeItem(UID_KEY);
  }

  // ==========================================
  //  用户信息
  // ==========================================

  /** 设置用户信息 */
  setUserInfo(userInfo: Record<string, unknown>): void {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  }

  /** 获取用户信息 */
  getUserInfo(): Record<string, unknown> | null {
    const raw = localStorage.getItem(USER_INFO_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** 清除用户信息 */
  clearUserInfo(): void {
    localStorage.removeItem(USER_INFO_KEY);
  }

  // ==========================================
  //  角色 & 入驻状态
  // ==========================================

  /** 设置角色 */
  setRole(role: string): void {
    localStorage.setItem(ROLE_KEY, role);
  }

  /** 获取角色 */
  getRole(): string {
    return localStorage.getItem(ROLE_KEY) || '';
  }

  /** 清除角色 */
  clearRole(): void {
    localStorage.removeItem(ROLE_KEY);
  }

  /** 设置入驻状态 */
  setOnboardingStatus(status: string): void {
    localStorage.setItem(ONBOARDING_STATUS_KEY, status);
  }

  /** 获取入驻状态 */
  getOnboardingStatus(): string {
    return localStorage.getItem(ONBOARDING_STATUS_KEY) || '';
  }

  /** 清除入驻状态 */
  clearOnboardingStatus(): void {
    localStorage.removeItem(ONBOARDING_STATUS_KEY);
  }

  // ==========================================
  //  是否已登录
  // ==========================================

  get isLoggedIn(): boolean {
    return !!this.getToken();
  }

  // ==========================================
  //  清除所有
  // ==========================================

  // ==========================================
  //  登出通知（应用层订阅后统一处理跳转）
  // ==========================================

  /**
   * 当登录态被清除时（401 / 主动退出），发出通知。
   * 由应用层（如 AppComponent）订阅，负责跳转逻辑。
   */
  readonly loggedOut$ = new Subject<void>();

  /** 清除所有登录态（用于退出登录 / 401 时），并通知应用层 */
  clearAll(): void {
    this.clearToken();
    this.clearUID();
    this.clearUserInfo();
    this.clearRole();
    this.clearOnboardingStatus();
    // 清空账号内存缓存（VIP 60s 短缓存 / 喊话权限 / AI 助手会话），防止下一账号读到上一账号状态
    this.vipService.clearCache();
    this.broadcastService.clearCache();
    this.aiChatSession.clear();
    this.loggedOut$.next();
  }
}
